import {QueryClient, useQuery, UseQueryResult} from "react-query";
import {APIPromise, APIResponse} from "../../services/HttpService";
import {UseQueryOptions} from "react-query/types/react/types";

    export interface IQueryOptions<T = any> {
        enabled?: boolean;
        noCache?: boolean;
        cacheTime?: number
        staleTime?: number;
        refetchInterval?: number | boolean;
        onSuccess?: (data?: T) => void;
        onError?: (data?: T) => void;
        retry?: boolean|number,
        refetchOnWindowFocus?: boolean;
    }


    export interface IMutationOptions {
        onSuccess?: (data?: any) => void;
        onFailure?: (data?: any) => void;
        onMutate?: (data?: any) => void;
    }

    export enum QueryKey {
        DATASET_CATEGORIES = "dataset-categories",
        DATASET_DATA_INFO = "dataset-data-info",
        DATASET_INFOS = "dataset-infos",
        USER_INFO = "user-info",
        BASE_DATASETS = "base-datasets",
        AIRFLOW_DAG_TASK_INFOS = "airflow-dag-task-infos",
        PRODUCT_TEMPLATE_INFOS = "product-template-infos",
        PAGINATED_DATASETS = "paginated-datasets-list",
        DATASETS_COUNT = "count",
        PRODUCT_TEMPLATE_CONFIGS = "product-template-configs",
        PRODUCT_TEMPLATE_DATA = "product-template-data",
        PRODUCT_TEMPLATE_PREVIEW_FILTER_DATA = "product-template-preview-filter-data",
        SCHEDULED_TASK_HISTORIES = "scheduled-task-histories",
        PRODUCT_TEMPLATE_FOREIGN_KEYS = "product-template-foreign-keys",
        DASHBOARD = "dashboard",
        GENERATED_REPORT_DATA = "generated-report-data",
        ALERT_FAN = "alert-fan"

    }

    export function defaultResponseHandler<T>(res: APIResponse<T>): T | undefined {
        if (!res.error) {
            return res.data;
        } else {
            console.log(res.error.message)
            throw res.error;
        }
    }

    const defaultQueryOptions: IQueryOptions = {
        enabled: true,
        noCache: false,
        refetchInterval: false,
    }

    export const defaultMutationOptions: IMutationOptions = {
        onSuccess: () => {},
        onFailure: () => {},
        onMutate: () => {},
    }

    export function useQueryWrapper<T>(
        queryKey: QueryKey | Array<QueryKey | string | null | undefined>,
        apiPromiseFn: () => APIPromise<T>,
        promiseHandler?: ((res: APIResponse<T>) => T) | null,
        options?: IQueryOptions
    ): UseQueryResult<T | undefined, Error> {
        const usedOptions = {...defaultQueryOptions, ...options};
        const usedPromiseHandler = promiseHandler ?? defaultResponseHandler;

        const queryOptions = {
            enabled: usedOptions.enabled,
            cacheTime: usedOptions.cacheTime ?? 1000 * 60 * 5,
            staleTime: usedOptions.staleTime ?? 1000 * 60,
            refetchInterval: usedOptions.refetchInterval,
            onSuccess: usedOptions.onSuccess,
            onError: usedOptions.onError,
            retry: usedOptions.retry,
            refetchOnWindowFocus: usedOptions.refetchOnWindowFocus,
        } as UseQueryOptions

        if (usedOptions.noCache) {
            queryOptions.cacheTime = 0;
            queryOptions.staleTime = 0;
        }

        return useQuery(queryKey, () => apiPromiseFn().then(usedPromiseHandler), queryOptions as any);
    }

    export const getRelatedCacheKeys = (queryClient: QueryClient, targetKey: string) => {
        return queryClient.getQueryCache().getAll()
            .map(query => query.queryKey)
            .filter(key => Array.isArray(key) ? key.includes(targetKey) : key === targetKey)
    }